/**
 * The site's pages (X-03, D439; site completion). After the `/{country}/{locale}` prefix (ADR-0030), one path segment may
 * name a page. The page id is neutral and stable (`drivers`, `account-deletion`, …); the URL words are the catalogs'
 * localized slugs, supplied by the caller — and ANY served language's slug reaches the page, so a link shared in another
 * language still works. The bare slug (before the soft redirect inserts the prefix) is the page too. Anything else renders
 * the home page, as the site always has. Pure; no market or language literal.
 */
import { parseEntryPath } from '@vello/web-entry';
import { isSupportedCountry, type SiteRegistry } from './registry.js';

/** Every page other than home, in nav/footer order. */
export const SITE_INNER_PAGES = ['drivers', 'business', 'safety', 'help', 'about', 'press', 'careers', 'sign-in', 'download', 'account-deletion'] as const;

export type SiteInnerPage = (typeof SITE_INNER_PAGES)[number];
export type SitePage = 'home' | SiteInnerPage;

/** The localized URL slugs of every page other than home, keyed by the neutral page id. */
export type SitePageSlugs = Readonly<Partial<Record<SiteInnerPage, readonly string[]>>>;

/** Whether a string names a site page. */
export function isSitePage(value: string): value is SitePage {
  return value === 'home' || (SITE_INNER_PAGES as readonly string[]).includes(value);
}

/** Which page a pathname addresses. */
export function sitePageOf(reg: SiteRegistry, pathname: string, slugs: SitePageSlugs): SitePage {
  const { rest } = parseEntryPath(pathname, { isCountry: (s) => isSupportedCountry(reg, s) });
  const segments = rest.split('/').filter((s) => s !== '');
  if (segments.length !== 1) return 'home';
  const segment = (segments[0] ?? '').toLowerCase();
  for (const page of SITE_INNER_PAGES) {
    if ((slugs[page] ?? []).some((w) => w.toLowerCase() === segment)) return page;
  }
  return 'home';
}
